import { GRID_CONSTANTS } from '../constants/GridConstants';
import {
  GridLevel,
  LayerType,
  TileType,
  encodeTileKey,
} from '../TileEditorTypes';
import { Entity } from '../TileEditorTypes';
import { validateGridLevel } from '../validation/GridLevelValidator';
import { gameLogger } from '../../utils/GameLogger';

export class LevelBuilder {
  private id: string;
  private name: string;
  private widthTiles: number;
  private heightTiles: number;
  private bgColor: string = GRID_CONSTANTS.DEFAULT_BG_COLOR;
  private gravity: number = GRID_CONSTANTS.DEFAULT_GRAVITY;
  private version: string = GRID_CONSTANTS.DEFAULT_VERSION;

  // Sparse tile storage per layer
  private groundTiles: Map<string, number> = new Map();
  private collisionTiles: Map<string, number> = new Map();
  private objectTiles: Map<string, number> = new Map();
  private triggerTiles: Map<string, number> = new Map();
  private entities: Entity[] = [];
  private spawnPoints: { x: number; y: number }[] = [];

  private constructor(id: string, widthTiles: number, heightTiles: number) {
    this.id = id;
    this.name = id;
    this.widthTiles = widthTiles;
    this.heightTiles = heightTiles;
  }

  /**
   * Create a new level builder.
   * @param id Unique level identifier (e.g. 'forest_1')
   * @param widthTiles Level width in tiles
   * @param heightTiles Level height in tiles
   */
  static create(id: string, widthTiles: number, heightTiles: number): LevelBuilder {
    return new LevelBuilder(id, widthTiles, heightTiles);
  }

  /** Set the display name */
  setName(name: string): this {
    this.name = name;
    return this;
  }

  /** Set background color (hex string) */
  setBackground(color: string): this {
    this.bgColor = color;
    return this;
  }

  /** Set gravity (default 1500) */
  setGravity(gravity: number): this {
    this.gravity = gravity;
    return this;
  }

  /** Set map version */
  setVersion(version: string): this {
    this.version = version;
    return this;
  }

  // ==========================================
  // Tile Placement — All in TILE coordinates
  // ==========================================

  /**
   * Place a horizontal ground row.
   * @param startX Starting tile X
   * @param y Tile Y position
   * @param width Number of tiles wide
   * @param tileType Tile type (default: FLOOR)
   */
  ground(startX: number, y: number, width: number, tileType: TileType = TileType.FLOOR): this {
    for (let x = startX; x < startX + width; x++) {
      this.groundTiles.set(encodeTileKey(x, y), tileType);
    }
    return this;
  }

  /**
   * Place a floating platform (same as ground, just semantic alias).
   */
  platform(startX: number, y: number, width: number, tileType: TileType = TileType.STONE): this {
    return this.ground(startX, y, width, tileType);
  }

  /**
   * Place a vertical wall.
   * @param x Tile X position
   * @param startY Starting tile Y
   * @param height Number of tiles tall
   * @param tileType Tile type (default: WALL)
   */
  wall(x: number, startY: number, height: number, tileType: TileType = TileType.WALL): this {
    for (let y = startY; y < startY + height; y++) {
      this.collisionTiles.set(encodeTileKey(x, y), tileType);
    }
    return this;
  }

  /**
   * Place a filled rectangle of tiles.
   * @param startX Top-left tile X
   * @param startY Top-left tile Y
   * @param width Width in tiles
   * @param height Height in tiles
   * @param tileType Tile type
   * @param layer Which layer to place on (default: GROUND)
   */
  rect(startX: number, startY: number, width: number, height: number, tileType: TileType, layer: 'ground' | 'collision' = 'ground'): this {
    const target = layer === 'collision' ? this.collisionTiles : this.groundTiles;
    for (let y = startY; y < startY + height; y++) {
      for (let x = startX; x < startX + width; x++) {
        target.set(encodeTileKey(x, y), tileType);
      }
    }
    return this;
  }

  /**
   * Place a single tile at a specific position.
   */
  tile(x: number, y: number, tileType: TileType, layer: LayerType = LayerType.GROUND): this {
    const target = this.getLayerMap(layer);
    target.set(encodeTileKey(x, y), tileType);
    return this;
  }

  /**
   * Place a rope (vertical climbable ladder).
   */
  rope(x: number, startY: number, height: number): this {
    for (let y = startY; y < startY + height; y++) {
      this.groundTiles.set(encodeTileKey(x, y), TileType.ROPE);
    }
    return this;
  }

  /**
   * Place a water pool.
   */
  water(startX: number, y: number, width: number, depth: number = 1): this {
    return this.rect(startX, y, width, depth, TileType.WATER);
  }

  /**
   * Place a lava pool.
   */
  lava(startX: number, y: number, width: number, depth: number = 1): this {
    return this.rect(startX, y, width, depth, TileType.LAVA);
  }

  // ==========================================
  // Entities & Spawns
  // ==========================================

  /**
   * Add a player spawn point.
   */
  spawn(x: number, y: number): this {
    this.spawnPoints.push({ x, y });
    return this;
  }

  /**
   * Place a teleporter entity.
   */
  teleporter(x: number, y: number, targetMapId: string, targetSpawnId?: string): this {
    this.entities.push({
      id: `tele_${x}_${y}`,
      type: 'teleporter',
      x,
      y,
      properties: {
        teleporterConfig: {
          targetMapId,
          targetSpawnId: targetSpawnId || 'default',
          activationType: 'manual',
        }
      }
    });
    return this;
  }

  /**
   * Define an NPC spawn point.
   */
  npcSpawn(x: number, y: number, npcType: string): this {
    this.entities.push({
      id: `spawn_npc_${npcType}_${x}_${y}`,
      type: 'npc_spawn',
      x,
      y,
      properties: { npcType }
    });
    return this;
  }

  /**
   * Define a Mob spawn point.
   */
  mobSpawn(x: number, y: number, mobType: string): this {
    this.entities.push({
      id: `spawn_mob_${mobType}_${x}_${y}`,
      type: 'mob_spawn',
      x,
      y,
      properties: { mobType }
    });
    return this;
  }

  // ==========================================
  // Build
  // ==========================================

  /**
   * Build the GridLevel. Validates and returns a clean result.
   * Throws if validation finds critical errors.
   */
  build(): GridLevel {
    // Add default spawn if none set
    if (this.spawnPoints.length === 0) {
      this.spawnPoints.push({ x: 5, y: Math.max(0, this.heightTiles - 5) });
    }

    const level: GridLevel = {
      id: this.id,
      name: this.name,
      version: this.version,
      widthTiles: this.widthTiles,
      heightTiles: this.heightTiles,
      tileSize: GRID_CONSTANTS.TILE_SIZE,
      backgroundColor: this.bgColor,
      gravity: this.gravity,
      spawnPoints: this.spawnPoints,
      layers: [
        { name: LayerType.GROUND, visible: true, locked: false, opacity: 1.0, tiles: this.groundTiles },
        { name: LayerType.COLLISION, visible: true, locked: false, opacity: 1.0, tiles: this.collisionTiles },
        { name: LayerType.OBJECTS, visible: true, locked: false, opacity: 1.0, tiles: this.objectTiles },
        { name: LayerType.TRIGGERS, visible: true, locked: false, opacity: 1.0, tiles: this.triggerTiles },
        { name: LayerType.ENTITIES, visible: true, locked: false, opacity: 1.0, tiles: new Map(), entities: this.entities },
      ],
    };

    // Validate
    const errors = validateGridLevel(level);
    const criticals = errors.filter(e => e.severity === 'error');
    if (criticals.length > 0) {
      gameLogger.error('level', `[LevelBuilder] Validation failed for "${this.id}":`);
      criticals.forEach(e => gameLogger.error('level', `  ❌ ${e.field}: ${e.message}`));
      throw new Error(`LevelBuilder: ${criticals.length} validation error(s) in "${this.id}"`);
    }

    const warnings = errors.filter(e => e.severity === 'warning');
    if (warnings.length > 0) {
      gameLogger.warn('level', `[LevelBuilder] ${warnings.length} warning(s) in "${this.id}":`);
      warnings.forEach(w => gameLogger.warn('level', `  ⚠️ ${w.field}: ${w.message}`));
    }

    const totalTiles = this.groundTiles.size + this.collisionTiles.size + this.objectTiles.size + this.triggerTiles.size;
    gameLogger.info('level', `[LevelBuilder] Built "${this.id}": ${totalTiles} tiles, ${this.entities.length} entities, ${this.spawnPoints.length} spawns`);

    return level;
  }

  // ==========================================
  // Internals
  // ==========================================

  private getLayerMap(layer: LayerType): Map<string, number> {
    switch (layer) {
    case LayerType.GROUND: return this.groundTiles;
    case LayerType.COLLISION: return this.collisionTiles;
    case LayerType.OBJECTS: return this.objectTiles;
    case LayerType.TRIGGERS: return this.triggerTiles;
    default: return this.groundTiles;
    }
  }
}
